/**
 * API module for communicating with the LinCal backend
 */

const API_BASE = '/api';

/**
 * Make an API request with automatic error handling
 * @param {string} path - API path
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>}
 */
async function request(path, options = {}) {
  const headers = { ...options.headers };
  
  // Only set Content-Type for requests with a body
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// Auth API - uses /auth/* routes directly (not /api/auth/*)
export const auth = {
  /**
   * Get current user info
   * @returns {Promise<{id: string, email: string, name?: string, avatarUrl?: string} | null>}
   */
  async me() {
    try {
      const response = await fetch('/auth/me', {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        return null;
      }
      return response.json();
    } catch {
      return null;
    }
  },

  /**
   * Redirect to login page (checks backend availability first)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async login() {
    try {
      // Check if backend is reachable with a short timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch('/api/calendars', {
        method: 'HEAD',
        credentials: 'include',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // Check if we got a valid response (not a proxy error)
      // Vite proxy returns 500 when backend is down
      if (response.status === 500) {
        return { success: false, error: 'offline' };
      }
      
      // If we get any other response (even 401), backend is up - proceed with redirect
      window.location.href = '/auth/login';
      return { success: true };
    } catch {
      // Network error or timeout - backend is completely offline
      return { success: false, error: 'offline' };
    }
  },

  /**
   * Logout current user
   */
  async logout() {
    await fetch('/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    window.location.reload();
  },
};

// Calendars API
export const calendars = {
  /**
   * Get all calendars (owned)
   * @returns {Promise<{owned: Array, shared: Array}>}
   */
  async list() {
    return request('/calendars');
  },

  /**
   * Get single calendar with all data (including included shared projects)
   * @param {string} id - Calendar ID
   * @returns {Promise<{calendar: object, projects: Array, events: Array, milestones: Array}>}
   */
  async get(id) {
    return request(`/calendars/${id}`);
  },

  /**
   * Create a new calendar
   * @param {object} data - Calendar data
   * @returns {Promise<object>}
   */
  async create(data) {
    return request('/calendars', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a calendar
   * @param {string} id - Calendar ID
   * @param {object} data - Update data
   * @returns {Promise<object>}
   */
  async update(id, data) {
    return request(`/calendars/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a calendar
   * @param {string} id - Calendar ID
   * @returns {Promise<{success: boolean}>}
   */
  async delete(id) {
    return request(`/calendars/${id}`, { method: 'DELETE' });
  },

  /**
   * Import data into a calendar
   * @param {string} id - Calendar ID
   * @param {object} data - Import data
   * @returns {Promise<{success: boolean}>}
   */
  async import(id, data) {
    return request(`/calendars/${id}/import`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Regenerate iCal token for a calendar
   * @param {string} id - Calendar ID
   * @returns {Promise<{token: string}>}
   */
  async regenerateIcalToken(id) {
    return request(`/calendars/${id}/ical/regenerate`, { method: 'POST' });
  },

  /**
   * Get iCal URL for a calendar
   * @param {string} token - iCal token
   * @returns {string}
   */
  getIcalUrl(token) {
    return `${window.location.origin}/ical/${token}`;
  },
};

// Projects API
export const projects = {
  /**
   * Get all projects for a calendar
   * @param {string} calendarId - Calendar ID
   * @returns {Promise<Array>}
   */
  async list(calendarId) {
    return request(`/calendars/${calendarId}/projects`);
  },

  /**
   * Create a project
   * @param {string} calendarId - Calendar ID
   * @param {object} data - Project data
   * @returns {Promise<object>}
   */
  async create(calendarId, data) {
    return request(`/calendars/${calendarId}/projects`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a project
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Project ID
   * @param {object} data - Update data
   * @returns {Promise<object>}
   */
  async update(calendarId, id, data) {
    return request(`/calendars/${calendarId}/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Reorder projects
   * @param {string} calendarId - Calendar ID
   * @param {string[]} orderedIds - Ordered project IDs
   * @returns {Promise<{success: boolean}>}
   */
  async reorder(calendarId, orderedIds) {
    return request(`/calendars/${calendarId}/projects/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  },

  /**
   * Delete a project
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Project ID
   * @returns {Promise<{success: boolean}>}
   */
  async delete(calendarId, id) {
    return request(`/calendars/${calendarId}/projects/${id}`, { method: 'DELETE' });
  },
};

// Events API
export const events = {
  /**
   * Get all events for a calendar
   * @param {string} calendarId - Calendar ID
   * @returns {Promise<Array>}
   */
  async list(calendarId) {
    return request(`/calendars/${calendarId}/events`);
  },

  /**
   * Create an event
   * @param {string} calendarId - Calendar ID
   * @param {object} data - Event data
   * @returns {Promise<object>}
   */
  async create(calendarId, data) {
    return request(`/calendars/${calendarId}/events`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an event
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Event ID
   * @param {object} data - Update data
   * @returns {Promise<object>}
   */
  async update(calendarId, id, data) {
    return request(`/calendars/${calendarId}/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete an event
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean}>}
   */
  async delete(calendarId, id) {
    return request(`/calendars/${calendarId}/events/${id}`, { method: 'DELETE' });
  },
};

// Milestones API
export const milestones = {
  /**
   * Get all milestones for a calendar
   * @param {string} calendarId - Calendar ID
   * @returns {Promise<Array>}
   */
  async list(calendarId) {
    return request(`/calendars/${calendarId}/milestones`);
  },

  /**
   * Create a milestone
   * @param {string} calendarId - Calendar ID
   * @param {object} data - Milestone data
   * @returns {Promise<object>}
   */
  async create(calendarId, data) {
    return request(`/calendars/${calendarId}/milestones`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a milestone
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Milestone ID
   * @param {object} data - Update data
   * @returns {Promise<object>}
   */
  async update(calendarId, id, data) {
    return request(`/calendars/${calendarId}/milestones/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a milestone
   * @param {string} calendarId - Calendar ID
   * @param {string} id - Milestone ID
   * @returns {Promise<{success: boolean}>}
   */
  async delete(calendarId, id) {
    return request(`/calendars/${calendarId}/milestones/${id}`, { method: 'DELETE' });
  },
};

// Shares API - Project-level sharing
export const shares = {
  /**
   * Get all shares for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>}
   */
  async listProjectShares(projectId) {
    return request(`/projects/${projectId}/shares`);
  },

  /**
   * Share a project with a user
   * @param {string} projectId - Project ID
   * @param {object} data - Share data { email, permission }
   * @returns {Promise<object>}
   */
  async shareProject(projectId, data) {
    return request(`/projects/${projectId}/shares`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update share permission
   * @param {string} shareId - Share ID
   * @param {string} permission - New permission (read|write)
   * @returns {Promise<{success: boolean}>}
   */
  async updateShare(shareId, permission) {
    return request(`/shares/${shareId}`, {
      method: 'PATCH',
      body: JSON.stringify({ permission }),
    });
  },

  /**
   * Remove a share
   * @param {string} shareId - Share ID
   * @returns {Promise<{success: boolean}>}
   */
  async deleteShare(shareId) {
    return request(`/shares/${shareId}`, { method: 'DELETE' });
  },

  /**
   * Get projects shared with current user
   * @returns {Promise<Array>}
   */
  async getSharedWithMe() {
    return request('/shared-with-me');
  },

  /**
   * Include a shared project in a calendar
   * @param {string} calendarId - Calendar ID
   * @param {string} projectId - Project ID to include
   * @returns {Promise<object>}
   */
  async includeProject(calendarId, projectId) {
    return request(`/calendars/${calendarId}/include`, {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
  },

  /**
   * Remove included project from calendar
   * @param {string} includedId - Included project ID
   * @returns {Promise<{success: boolean}>}
   */
  async removeIncluded(includedId) {
    return request(`/included/${includedId}`, { method: 'DELETE' });
  },

  /**
   * Update included project customizations (name, color)
   * @param {string} includedId - Included project ID
   * @param {object} updates - { customName?: string, customColor?: string }
   * @returns {Promise<{success: boolean}>}
   */
  async updateIncluded(includedId, updates) {
    return request(`/included/${includedId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Get recent contacts for quick sharing
   * @returns {Promise<Array>}
   */
  async getRecentContacts() {
    return request('/contacts/recent');
  },

  /**
   * Search users by email
   * @param {string} query - Search query
   * @returns {Promise<Array>}
   */
  async searchUsers(query) {
    return request(`/users/search?q=${encodeURIComponent(query)}`);
  },
};

export default {
  auth,
  calendars,
  projects,
  events,
  milestones,
  shares,
};
